import type { ComboboxData, ComboboxItemGroup } from '@any-ui-react/core';
import groupBy from 'lodash-es/groupBy';
import map from 'lodash-es/map';
import { useEffect, useMemo } from 'react';
import {
  type EmployeeAlertPriority,
  type EmployeeAlertReasonType,
  type ListEmployeeAlertReasonTypesQuery,
  type ListEmployeeAlertReasonTypesQueryVariables,
  useListEmployeeAlertReasonTypesQuery,
} from '@/common/graphql';
import type { ComboboxItemWithFormatted, CommonProps, UseQueryReturn } from '../props';
import { SelectMultiple, type SelectMultipleProps } from '../SelectMultiple';

type Data = ComboboxItemWithFormatted<EmployeeAlertReasonType> &
  Omit<ListEmployeeAlertReasonTypesQuery['listEmployeeAlertReasonTypes'][number], 'value'>;
type Props = CommonProps<never> & {
  priorities: EmployeeAlertPriority[];
};

const useQuery = (
  onLoadingCompleted?: () => void,
): UseQueryReturn<ListEmployeeAlertReasonTypesQueryVariables, Data> => {
  const { data, ...rest } = useListEmployeeAlertReasonTypesQuery({ fetchPolicy: 'cache-first' });

  useEffect(() => {
    if (onLoadingCompleted) onLoadingCompleted();
  }, [onLoadingCompleted]);

  return {
    data:
      data?.listEmployeeAlertReasonTypes?.map(it => ({
        ...it,
        value: it.value,
        label: it.displayName,
        priority: it.priority,
      })) || [],
    ...rest,
  };
};

const useGroupedData = (flatData: Data[], priorities: EmployeeAlertPriority[]): ComboboxData =>
  useMemo(
    () =>
      map(
        groupBy(
          flatData.filter(it => (priorities.length === 0 ? true : priorities.includes(it.priority))),
          'priority',
        ),
        items =>
          ({
            group: items[0].priority,
            items: [...items].sort((a, b) => a.displayName.localeCompare(b.displayName)),
          }) as ComboboxItemGroup,
      ),
    [flatData, priorities],
  );

export const EmployeeAlertReasonSelectMultiple = ({
  placeholder = 'Reason',
  priorities,
  ...props
}: Props & SelectMultipleProps<Data>) => {
  const { data: flatData, loading } = useQuery(props.onLoadingCompleted);
  const grouped = useGroupedData(flatData, priorities);
  return (
    <SelectMultiple
      placeholder={placeholder}
      maxItemVisible={10}
      multipleSelectedLabel={length => `${length} reasons`}
      {...props}
      data={grouped as unknown as Data[]}
      loading={loading}
    />
  );
};
